import {useAnalytics} from '@github-ui/use-analytics'
import {AgentIcon, CopilotIcon, PencilIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, type ButtonProps} from '@primer/react'
import {
  useAgentMergeConflictResolution,
  type AgentMergeConflictResolutionOptions,
} from '@github-ui/copilot_agent_merge/hooks/use-agent-merge-conflict-resolution'
import {useEffect, type SyntheticEvent} from 'react'

import type {ConflictMergeConditionPayload} from '../../../page-data/payloads/merge-box'
import {useAgentConflictResolutionComment} from '../../../hooks/mutations/use-agent-conflict-resolution-comment'

export type ConflictResolutionAction =
  | 'edit_conflicts_on_web'
  | 'fix_conflicts_with_copilot'
  | 'resolve_conflicts_with_agent_merge'

/**
 *
 * Offers the web editor, a single Copilot session, or ongoing Agent Merge conflict resolution.
 */
export function ResolveConflictsAgentMenu({
  buttonId,
  conflictsWebEditorPath,
  onError,
  onAgentMergeError,
  onActionSelect,
  onFixWithCopilot,
  agentMerge,
  webEditorConflictResolution,
  size,
}: {
  buttonId?: string
  conflictsWebEditorPath: string
  onError?: (message: string) => void
  onAgentMergeError?: (message: string | null) => void
  onActionSelect?: (actionName: ConflictResolutionAction) => void
  onFixWithCopilot?: () => void
  agentMerge?: AgentMergeConflictResolutionOptions
  webEditorConflictResolution: NonNullable<ConflictMergeConditionPayload['webEditorConflictResolution']>
  size?: ButtonProps['size']
}) {
  const {sendAnalyticsEvent} = useAnalytics()
  const {startAgentConflictResolutionComment, isAgentConflictResolutionCommentPending} =
    useAgentConflictResolutionComment({
      onSuccess: () => {
        sendAnalyticsEvent('conflicts_section.start_agent_session', 'MERGEBOX_CONFLICTS_SECTION_START_AGENT_SESSION')
      },
      onError: () => {
        onError?.('Failed to start an agent session. Please try again.')
      },
    })

  const webEditorItemInactiveText = !webEditorConflictResolution.viewerCanResolve
    ? webEditorConflictResolution.viewerCannotResolve?.message
    : undefined
  const handleActionSelect = (event: SyntheticEvent, actionName: ConflictResolutionAction) => {
    if (!onActionSelect) return
    event.stopPropagation()
    onActionSelect(actionName)
  }
  const handleFixWithCopilot = () => {
    if (onFixWithCopilot) {
      onFixWithCopilot()
      sendAnalyticsEvent('conflicts_section.start_agent_session', 'MERGEBOX_CONFLICTS_SECTION_START_AGENT_SESSION')
      return
    }

    startAgentConflictResolutionComment()
  }

  const menu = (agentMergeControl?: ReturnType<typeof useAgentMergeConflictResolution>) => (
    <ActionMenu>
      <ActionMenu.Button
        id={buttonId}
        loading={(!onFixWithCopilot && isAgentConflictResolutionCommentPending) || agentMergeControl?.isMutating}
        size={size}
      >
        Resolve conflicts
      </ActionMenu.Button>
      <ActionMenu.Overlay align="end">
        <ActionList>
          <ActionList.LinkItem
            href={conflictsWebEditorPath}
            inactiveText={webEditorItemInactiveText}
            onClick={event => handleActionSelect(event, 'edit_conflicts_on_web')}
          >
            <ActionList.LeadingVisual>
              <PencilIcon />
            </ActionList.LeadingVisual>
            Edit on the web
          </ActionList.LinkItem>
          <ActionList.Item
            onSelect={event => {
              handleActionSelect(event, 'fix_conflicts_with_copilot')
              handleFixWithCopilot()
            }}
          >
            <ActionList.LeadingVisual>
              <AgentIcon />
            </ActionList.LeadingVisual>
            Fix with Copilot
          </ActionList.Item>
          {agentMergeControl?.visible && (
            <ActionList.Item
              disabled={!agentMergeControl.canEnable && !agentMergeControl.retryLoad}
              onSelect={event => {
                if (agentMergeControl.retryLoad) {
                  void agentMergeControl.retryLoad()
                  return
                }
                handleActionSelect(event, 'resolve_conflicts_with_agent_merge')
                agentMergeControl.enable()
                sendAnalyticsEvent(
                  'conflicts_section.enable_agent_merge',
                  'MERGEBOX_CONFLICTS_SECTION_ENABLE_AGENT_MERGE',
                )
              }}
            >
              <ActionList.LeadingVisual>
                <CopilotIcon />
              </ActionList.LeadingVisual>
              {agentMergeControl.retryLoad ? 'Retry loading Agent Merge' : 'Watch with Copilot'}
              <ActionList.Description variant="block">
                {agentMergeControl.retryLoad
                  ? 'Load Agent Merge settings to try again.'
                  : agentMergeControl.alreadyResolving
                    ? 'Automatic merge conflict resolution is already enabled.'
                    : 'Automatically resolve merge conflicts in this pull request.'}
              </ActionList.Description>
            </ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )

  return agentMerge ? (
    <AgentMergeConflictMenu {...agentMerge} onAgentMergeError={onAgentMergeError}>
      {menu}
    </AgentMergeConflictMenu>
  ) : (
    menu()
  )
}

function AgentMergeConflictMenu({
  children,
  onAgentMergeError,
  ...options
}: AgentMergeConflictResolutionOptions & {
  children: (control: ReturnType<typeof useAgentMergeConflictResolution>) => React.ReactNode
  onAgentMergeError?: (message: string | null) => void
}) {
  const control = useAgentMergeConflictResolution(options)
  const errorMessage = control.visible ? control.errorMessage : null
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- Report async errors outside the caller's constrained action slot.
    onAgentMergeError?.(errorMessage)
  }, [errorMessage, onAgentMergeError])

  return (
    <>
      {children(control)}
      {!onAgentMergeError && errorMessage && (
        <Banner variant="critical">
          <Banner.Title>Could not update Agent Merge</Banner.Title>
          <Banner.Description>{errorMessage}</Banner.Description>
        </Banner>
      )}
    </>
  )
}
